import { useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useT } from "../../../../i18n";
import { toNumberOrNull } from "../../../../utils/energyDevices";
import { formatWatt } from "./EnergyKpi";
import EnergyTimelineModal from "./EnergyTimelineModal";
import {
  AXIS_TICK,
  CHART_HEIGHT,
  CHART_MARGIN,
  GRID_STROKE,
  Y_AXIS_WIDTH,
  unitLabel,
} from "./chartStyle";
import "./EnergyInsight.css";

// 실측 6시간(실선) + 예측 6시간(점선)을 한 축에 그린다. 점은 1시간, 눈금은 2시간.
// 눈금은 실제 시각(09시 · 11시 · 13시 …)으로 적는다 — "-2h / 지금 / +2h" 만 있으면 지금이 몇 시인지 알 수 없다.
// "지금" 세로선은 현재 시간대 눈금 위에 선다.
// history 는 BE 가 BigQuery 시간 평균으로 만든다(-6h ~ 지금 시간대, 7점). forecast 는 AI 값(+1h ~ +6h).
// forecastHistory 는 당시에 저장한 +1시간 예측이다. 실측과 같은 시각에 점선으로 겹쳐 비교한다.
// 둘 다 비면 그리지 않는다. 한쪽만 있어도 축은 -6h ~ +6h 로 고정해 비는 쪽(예측 대기)이 보이게 둔다.
// 높이 · 축 스타일은 추이 차트와 같다(chartStyle.js) — 어디에 놓여도 같은 크기.
//
// metric — 어떤 선을 그릴지. 시리즈마다 실측 실선 + 예측 점선.
//   "combined" — 큐브. 발전(초록) + 소비(파랑). 선 4개.
//   "pole"     — 스마트폴. 태양광(초록) + 풍력(파랑) + 소비(주황). 선 6개.
//                소비는 하나다 — 태양광 · 풍력 발전이 배터리 하나로 모여 어느 쪽 소비인지 나눌 수 없다. 소비도 예측 대상.
//
// 오른쪽 위 + 버튼은 같은 행으로 크게 보는 모달(EnergyTimelineModal)을 연다. deviceLabel 은 모달 부제에 쓴다.

const HOURS = 6;
const HOUR_MS = 60 * 60 * 1000;

const METRICS = {
  gen: { histKey: "genHist", fcKey: "genFc", field: "genW", color: "#4ad4a8", labelKey: "energy.timeline.gen", measuredKey: "energy.timeline.genMeasured", forecastKey: "energy.timeline.genForecast" },
  cons: { histKey: "consHist", fcKey: "consFc", field: "consW", color: "#78aee8", labelKey: "energy.timeline.cons", measuredKey: "energy.timeline.consMeasured", forecastKey: "energy.timeline.consForecast" },
  solar: { histKey: "solarHist", fcKey: "solarFc", field: "solarW", color: "#4ad4a8", labelKey: "energy.timeline.solar", measuredKey: "energy.timeline.solarMeasured", forecastKey: "energy.timeline.solarForecast" },
  wind: { histKey: "windHist", fcKey: "windFc", field: "windW", color: "#78aee8", labelKey: "energy.timeline.wind", measuredKey: "energy.timeline.windMeasured", forecastKey: "energy.timeline.windForecast" },
};
// 폴 차트의 소비는 주황 — 태양광(초록) · 풍력(파랑)과 색이 겹치지 않게. PowerGeneration 타일과 같은 색.
const POLE_CONS = { ...METRICS.cons, color: "#FFA500" };
const FORECAST_LEGEND_COLOR = "#93a5b6";

const pad = (value) => String(value).padStart(2, "0");

const hourOffset = (ts, nowHour) => {
  const time = new Date(ts).getTime();
  if (Number.isNaN(time)) return null;
  return Math.round((time - nowHour) / HOUR_MS);
};

// 값이 있는 줄만 보여준다.
const TimelineTooltip = ({ active, payload, label }) => {
  const t = useT();
  if (!active || !payload?.length) return null;
  const rows = payload.filter((item) => item.value != null);
  if (!rows.length) return null;
  return (
    <div className="tooltip-container">
      <p className="tooltip-label">{label}</p>
      {rows.map((item) => (
        <p key={item.dataKey} className="tooltip-value" style={{ color: item.stroke ?? item.color }}>
          {item.payload?.offset <= 0 && item.dataKey.endsWith("Fc")
            ? t("energy.timeline.pastForecast", { name: item.name }) : item.name} {formatWatt(item.value)} W
        </p>
      ))}
    </div>
  );
};

const solidLine = (dataKey, name, color, singlePoint) => (
  <Line
    key={dataKey}
    type="monotone"
    dataKey={dataKey}
    name={name}
    stroke={color}
    strokeWidth={2}
    dot={singlePoint ? { r: 2.5, strokeWidth: 0, fill: color } : false}
    isAnimationActive={false}
  />
);

const dashedLine = (dataKey, name, color) => (
  <Line
    key={dataKey}
    type="monotone"
    dataKey={dataKey}
    name={name}
    stroke={color}
    strokeWidth={2}
    strokeDasharray="5 4"
    dot={{ r: 2.5, strokeWidth: 0, fill: color }}
    isAnimationActive={false}
  />
);

export default function EnergyTimelineChart({
  history = [],
  forecast = [],
  forecastHistory = [],
  embedded = false,
  metric = "combined",
  forecastOrigin = null,
  comparisonHour = null,
  consumptionPending = false,
  deviceLabel = null,
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const expandRef = useRef(null);
  const measured = (Array.isArray(history) ? history : []).filter((point) => point && point.ts);
  const predicted = (Array.isArray(forecast) ? forecast : []).filter((point) => point && point.ts);
  const past = (Array.isArray(forecastHistory) ? forecastHistory : []).filter((point) => point && point.ts);
  if (!measured.length && !predicted.length && !past.length && !consumptionPending) return null;

  // Use the server's current comparison hour; keep every forecast's actual target time.
  const fallbackNow = new Date();
  fallbackNow.setMinutes(0, 0, 0);
  const originTime = comparisonHour || forecastOrigin ? new Date(comparisonHour || forecastOrigin).getTime() : NaN;
  const nowHour = Number.isFinite(originTime) ? originTime : fallbackNow.getTime();
  // 눈금 = 그 시간대의 시각. 같은 12시간 안에서는 겹치지 않아 데이터 키로도 쓴다.
  const labelFor = (offset) =>
    t("energy.timeline.hourTick", { h: pad(new Date(nowHour + (offset + 9) * HOUR_MS).getUTCHours()) });
  // 세로선 라벨은 "지금" 만. 몇 시인지는 바로 아래 눈금(15시)이 말해 준다.
  const nowLabel = t(forecastOrigin && !comparisonHour ? "energy.forecast.reference" : "energy.timeline.now");
  // Zero is a display fallback only; source forecasts and measured consumption stay untouched.
  const consumptionForDisplay = (value) => toNumberOrNull(value) ?? (consumptionPending ? 0 : null);

  const slots = new Map();
  for (let offset = -HOURS; offset <= HOURS; offset += 1) {
    slots.set(offset, {
      offset,
      label: labelFor(offset),
      genHist: null, consHist: null, solarHist: null, windHist: null,
      genFc: null, consFc: null, solarFc: null, windFc: null,
    });
  }
  measured.forEach((point) => {
    const row = slots.get(hourOffset(point.ts, nowHour));
    if (!row) return;
    row.genHist = toNumberOrNull(point.genW);
    row.consHist = toNumberOrNull(point.consW);
    row.solarHist = toNumberOrNull(point.solarW);
    row.windHist = toNumberOrNull(point.windW);
  });
  // 행에는 소비 0 대체값이 들어가므로, 예측 오차는 모달이 이 원본으로 계산한다.
  const rawFc = new Map();
  [...past.filter((point) => hourOffset(point.ts, nowHour) <= 0),
    ...predicted.filter((point) => hourOffset(point.ts, nowHour) > 0)].forEach((point) => {
    const offset = hourOffset(point.ts, nowHour);
    const row = slots.get(offset);
    if (!row) return;
    rawFc.set(offset, point);
    row.genFc = toNumberOrNull(point.genW);
    row.consFc = consumptionForDisplay(point.consW);
    row.solarFc = toNumberOrNull(point.solarW);
    row.windFc = toNumberOrNull(point.windW);
  });
  // Missing past issues remain gaps; measured values never stand in for a forecast.

  const data = [...slots.values()];
  const ticks = data.filter((row) => row.offset % 2 === 0).map((row) => row.label);
  const next = predicted.find((point) => hourOffset(point.ts, nowHour) === 1);

  const isPole = metric === "pole";
  const series = isPole ? [METRICS.solar, METRICS.wind, POLE_CONS]
    : [METRICS.gen, consumptionPending ? POLE_CONS : METRICS.cons];

  // 실선을 먼저, 점선을 나중에 그려 점선이 위에 오게 한다.
  const lines = [
    ...series.map((item) => solidLine(item.histKey, t(item.measuredKey), item.color, measured.length === 1)),
    ...series.map((item) => dashedLine(item.fcKey, t(item.forecastKey), item.color)),
  ];

  // 오른쪽 상태: 예측이 있으면 +1h 값, 없으면 "AI 연동 후".
  let nextText = t("energy.timeline.forecastPending");
  if (next && isPole) {
    nextText = t("energy.timeline.nextPole", {
      solar: formatWatt(next.solarW),
      wind: formatWatt(next.windW),
      cons: formatWatt(consumptionForDisplay(next.consW)),
    });
  } else if (next) {
    nextText = t("energy.timeline.next", { gen: formatWatt(next.genW), cons: formatWatt(consumptionForDisplay(next.consW)) });
  }

  const placeholderSegment = consumptionPending && !predicted.length
    ? [{ x: labelFor(1), y: 0 }, { x: labelFor(HOURS), y: 0 }]
    : null;
  const closeExpanded = () => {
    setExpanded(false);
    expandRef.current?.focus();
  };

  const body = (
    <div className="ei-timeline">
      {/* "실측 6시간 · 예측 6시간" 같은 제목은 두지 않는다 — 축의 시각과 "지금" 선이 이미 말해 준다. */}
      <div className="ei-forecast-head ei-forecast-head--right">
        <span className="ei-forecast-next">{nextText}</span>
        <button
          ref={expandRef}
          type="button"
          className="ei-expand-btn"
          aria-label={t("energy.timeline.expand")}
          title={t("energy.timeline.expand")}
          aria-haspopup="dialog"
          onClick={() => setExpanded(true)}
        >
          <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">
            <path d="M7 2v10M2 7h10" />
          </svg>
        </button>
      </div>
      <div className="ei-forecast-chart">
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="label"
              ticks={ticks}
              interval={0}
              tick={AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={Y_AXIS_WIDTH} label={unitLabel("W")} />
            <Tooltip content={<TimelineTooltip />} />
            <ReferenceLine
              x={labelFor(0)}
              stroke="rgba(230, 237, 244, 0.35)"
              strokeDasharray="3 3"
              label={{ value: nowLabel, position: "top", fill: "#e6edf4", fontSize: 11, fontWeight: 600 }}
            />
            {lines}
            {/* Keep the empty chart layout while the whole forecast is unavailable. */}
            {placeholderSegment && <ReferenceLine
              segment={placeholderSegment}
              stroke="#FFA500" strokeWidth={2} strokeDasharray="5 4"
            />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="ei-split-legend ei-timeline-legend">
        {series.map((item) => (
          <span key={item.field} className="ei-legend-key">
            <i className="solid" style={{ borderColor: item.color }} />
            {t(item.labelKey)}
          </span>
        ))}
        <span className="ei-legend-key">
          <i className="dash" style={{ borderColor: FORECAST_LEGEND_COLOR }} />
          {t("energy.timeline.forecast")}
        </span>
      </div>
      {expanded && (
        <EnergyTimelineModal
          onClose={closeExpanded}
          deviceLabel={deviceLabel}
          nowHour={nowHour}
          nowLabel={nowLabel}
          nowTick={labelFor(0)}
          endTick={labelFor(HOURS)}
          data={data}
          series={series}
          rawFc={rawFc}
          placeholderSegment={placeholderSegment}
        />
      )}
    </div>
  );

  return embedded ? body : <div className="panel ei-panel">{body}</div>;
}
